import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeftIcon as ArrowLeft, TreeStructureIcon as Tree, ArrowsClockwiseIcon as ArrowsClockwise, CircleNotchIcon as Spinner, MonitorIcon as Monitor, ListNumbersIcon as ListNumbers } from "@phosphor-icons/react";
import { getWorkspace, listFieldReferences, listWorkspaceMembers, rebuildFieldReferences } from "@/lib/api";
import { formatApiErrorDetail, useAuth } from "@/lib/auth";
import FieldDepsSummaryCards from "@/components/fielddeps/FieldDepsSummaryCards";
import IoMixStrip from "@/components/fielddeps/IoMixStrip";
import ScreenFieldsCard from "@/components/fielddeps/ScreenFieldsCard";
import FieldImpactPanel from "@/components/fielddeps/FieldImpactPanel";
import FieldRefFilters from "@/components/fielddeps/FieldRefFilters";
import FieldRefTable from "@/components/fielddeps/FieldRefTable";
import FieldRefDetail from "@/components/fielddeps/FieldRefDetail";

const PAGE = 100;
const FILTER_KEYS = ["q", "field", "program", "file", "reference_type", "origin", "confidence", "ambiguous", "display_file"];

export default function WorkspaceFieldDependencies() {
  const { id: workspaceId } = useParams();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [selectedId, setSelectedId] = useState(() => Number(params.get("ref")) || null);

  const filters = useMemo(() => Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) || ""])), [params]);
  const offset = Number(params.get("offset")) || 0;
  const impactField = params.get("impact") || "";
  const impactFile = params.get("impact_file") || "";
  const canWrite = user?.role === "admin" || myRole === "owner" || myRole === "editor";

  useEffect(() => {
    getWorkspace(workspaceId).then((d) => setWorkspace(d?.workspace || null)).catch(() => {});
    listWorkspaceMembers(workspaceId)
      .then((m) => setMyRole((m.members || []).find((x) => x.user_id === user?.id)?.role || null))
      .catch(() => setMyRole(null));
  }, [workspaceId, user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = { limit: PAGE, offset };
      for (const k of FILTER_KEYS) if (filters[k]) query[k] = filters[k];
      setPage(await listFieldReferences(workspaceId, query));
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not load field references");
    } finally {
      setLoading(false);
    }
  }, [workspaceId, offset, filters]);
  useEffect(() => { load(); }, [load]);

  const updateParams = (patch, { replace = true } = {}) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === "" || v === null || v === undefined) next.delete(k); else next.set(k, String(v));
    }
    setParams(next, { replace });
  };
  const setFilters = (f) => updateParams({ ...f, offset: "" });
  const clearFilters = () => updateParams(Object.fromEntries([...FILTER_KEYS, "offset"].map((k) => [k, ""])));
  const openRef = (id) => { setSelectedId(id); updateParams({ ref: id }); };
  const closeRef = () => { setSelectedId(null); updateParams({ ref: "" }); };
  const searchImpact = (field, file) => updateParams({ impact: field, impact_file: file || "" }, { replace: false });

  const handleRebuild = async () => {
    if (!window.confirm("Rebuild field references from the current evidence? Deterministic rows are upserted (no duplicates); rows whose evidence vanished are removed; manual rows are never touched.")) return;
    setRebuilding(true);
    try {
      const r = await rebuildFieldReferences(workspaceId);
      toast.success(`Rebuilt: ${r.inserted} new, ${r.updated} updated, ${r.deleted} removed, ${r.unchanged} unchanged · ${r.total} total (${r.ambiguous} ambiguous${r.manual_preserved ? `, ${r.manual_preserved} manual kept` : ""}).`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Rebuild failed");
    } finally {
      setRebuilding(false);
    }
  };

  const totalPages = page ? Math.max(1, Math.ceil(page.total / PAGE)) : 1;
  const pageNum = Math.floor(offset / PAGE);

  return (
    <div className="space-y-6" data-testid="field-deps-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to={`/workspaces/${workspaceId}/metadata`} className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] hover:text-[var(--ml-primary)] inline-flex items-center gap-1" data-testid="fd-back-to-metadata">
            <ArrowLeft size={11} weight="bold" /> Back to metadata repository
          </Link>
          <h1 className="text-3xl sm:text-4xl font-semibold leading-tight tracking-tight mt-3">
            <Tree size={26} weight="duotone" color="#0f62fe" className="inline-block mr-2 align-text-bottom" />
            Field dependencies
          </h1>
          <p className="text-sm text-[var(--ml-text-secondary)] mt-1 max-w-3xl">
            Which programs touch a field, which rules reference it, which files contain it, which screens expose it and what evidence backs each link.
            Built only from deterministic sources — parsed rule conditions, RPG call parameters and field I/O, DDS key / join facts, display-file (DSPF) usage codes — and
            resolved against the DSPFFD catalog. Ambiguous field names stay unresolved on purpose; nothing here is LLM inference.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] text-right">
            Workspace<br /><span className="text-[var(--ml-text-primary)]">{workspace?.name || "—"}</span>
          </div>
          {canWrite && (
            <button type="button" onClick={handleRebuild} disabled={rebuilding} className="ml-btn-secondary flex items-center gap-1.5" data-testid="fd-rebuild-btn"
              title="Recompute from business rules, parsed source and DSPFFD. Idempotent.">
              {rebuilding ? <Spinner size={14} className="animate-spin" /> : <ArrowsClockwise size={14} weight="bold" />}
              {rebuilding ? "Rebuilding…" : "Rebuild references"}
            </button>
          )}
          <Link to={`/workspaces/${workspaceId}/metadata/screen-bindings`} className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline inline-flex items-center gap-1" data-testid="fd-screen-bindings-link">
            <Monitor size={11} weight="bold" /> Screen format bindings →
          </Link>
          <Link to={`/workspaces/${workspaceId}/metadata/screen-walkthrough`} className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline inline-flex items-center gap-1" data-testid="fd-walkthrough-link">
            <ListNumbers size={11} weight="bold" /> Screen walkthrough →
          </Link>
        </div>
      </div>

      <FieldDepsSummaryCards summary={page?.summary} />
      {page?.summary && <IoMixStrip io={page.summary.io} />}
      {page?.summary && <ScreenFieldsCard screen={page.summary.screen} topDisplayFiles={page.top_display_files} onFilter={setFilters} />}

      <FieldImpactPanel workspaceId={workspaceId} field={impactField} file={impactFile} onSearch={searchImpact} onOpenReference={openRef} />

      <FieldRefFilters filters={filters} onChange={setFilters} onClear={clearFilters} />

      <div className={`grid grid-cols-1 gap-4 ${selectedId ? "lg:grid-cols-3" : ""}`}>
        <div className={selectedId ? "lg:col-span-2 space-y-3" : "space-y-3"}>
          <FieldRefTable page={page} loading={loading} workspaceId={workspaceId} onSelect={openRef} selectedId={selectedId} canRebuild={canWrite} onRebuild={handleRebuild} />
          {page && page.total > PAGE && (
            <div className="flex items-center justify-between text-sm" data-testid="fd-pagination">
              <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Page {pageNum + 1} of {totalPages} · {page.total} rows</div>
              <div className="flex gap-2">
                <button onClick={() => updateParams({ offset: Math.max(0, offset - PAGE) || "" })} disabled={pageNum === 0} className="ml-btn-ghost disabled:opacity-40" data-testid="fd-prev">← Prev</button>
                <button onClick={() => updateParams({ offset: offset + PAGE })} disabled={pageNum >= totalPages - 1} className="ml-btn-ghost disabled:opacity-40" data-testid="fd-next">Next →</button>
              </div>
            </div>
          )}
        </div>
        {selectedId && <FieldRefDetail workspaceId={workspaceId} referenceId={selectedId} onClose={closeRef} />}
      </div>
    </div>
  );
}
